import { bcLong } from '@lib2/model/util/string/BaseConverter';
import { LIST_TYPE_LABELS, LIST_SORT_UI_OPTIONS } from '@/lib/constants/playlist';
import type { MediaStateType } from '@/lib/types/Media';
import type { VideoViewData } from '@/lib/types/Media';
import type {
  AnyPlaylistData, AnyPlaylistView, CustomPlaylistData, CustomPlaylistView,
  TagPlaylistData, TagPlaylistView,
  PlaylistGridRow, PlaylistListResponse, PlaylistMediaPosition,
  ListTagRule, TagSearchRule,
  PlaylistSettingsFormData, TagPlaylistContentFormData,
  UpdateCustomPlaylistRequest, UpdateTagPlaylistRequest,
} from '@/lib/types/Playlist';
import type { TagLogicalOperator } from '@/lib/constants/playlist';

export class PlaylistUtils {

  // --- Grid transforms ---

  /**
   * Compute the approximate number of videos matching a tag playlist's rules.
   * Applies tag rule logic (and/or/not) against cached video data, filtered by mediaType.
   * Returns null if videos are not yet loaded.
   */
  static computeTagMatchCount(playlist: TagPlaylistView, videos: VideoViewData[] | undefined): number | null {
    if (!videos) return null;

    const candidates = videos.filter(v => v.media.mediaType === playlist.mediaType);

    const andTags = playlist.tagRules.filter(r => r.rule === 'and').map(r => r.tag).filter(Boolean);
    const orTags = playlist.tagRules.filter(r => r.rule === 'or').map(r => r.tag).filter(Boolean);
    const notTags = playlist.tagRules.filter(r => r.rule === 'not').map(r => r.tag).filter(Boolean);

    const matched = candidates.filter(video => {
      const videoTags = video.tags || [];
      if (andTags.length > 0 && !andTags.every(t => videoTags.includes(t))) return false;
      if (orTags.length > 0 && !orTags.some(t => videoTags.includes(t))) return false;
      if (notTags.length > 0 && notTags.some(t => videoTags.includes(t))) return false;
      return true;
    }).length;

    return playlist.limit != null ? Math.min(matched, playlist.limit) : matched;
  }

  /** Convert a single playlist to a grid row for display */
  static toGridRow(playlist: AnyPlaylistView, videos?: VideoViewData[]): PlaylistGridRow {
    const itemCount = playlist.listType === 'custom'
      ? (playlist as CustomPlaylistView).mediaPositions.length
      : PlaylistUtils.computeTagMatchCount(playlist as TagPlaylistView, videos);
    return {
      listId: playlist.listId,
      displayId: bcLong.getStr(playlist.listId),
      title: playlist.title,
      listType: playlist.listType,
      listTypeLabel: LIST_TYPE_LABELS[playlist.listType],
      state: playlist.mediaStateType,
      mediaType: playlist.mediaType,
      itemCount,
      created: playlist.created,
      updated: playlist.updated,
      isActive: playlist.mediaStateType === 'active',
    };
  }

  /**
   * Merge customLists + tagLists into a single array of grid rows.
   * Pass videos (from useVideos() cache) to compute tag playlist match counts.
   */
  static toGridRows(response: PlaylistListResponse, videos?: VideoViewData[]): PlaylistGridRow[] {
    const allPlaylists: AnyPlaylistView[] = [
      ...response.customLists,
      ...response.tagLists,
    ];
    return allPlaylists.map(p => PlaylistUtils.toGridRow(p, videos));
  }

  // --- Tag rule conversion ---

  /**
   * Convert flat backend format [{tag, rule}...] to grouped UI format [{lop, values[]}...]
   * Re-implements TagListView2.getTagRules() from lib2 as a pure function.
   */
  static tagRulesToSearchRules(flat: ListTagRule[]): TagSearchRule[] {
    const grouped = new Map<string, string[]>();
    for (const { tag, rule } of flat) {
      const existing = grouped.get(rule);
      if (existing) {
        if (tag) existing.push(tag);
      } else {
        grouped.set(rule, tag ? [tag] : []);
      }
    }
    return Array.from(grouped.entries()).map(([lop, values]) => ({
      lop: lop as TagLogicalOperator,
      values,
    }));
  }

  /**
   * Convert grouped UI format [{lop, values[]}...] back to flat [{tag, rule}...] for API.
   * Empty values arrays produce {rule: lop, tag: ''} to preserve the rule in the backend.
   * Re-implements TagListView2.setTagRules() from lib2 as a pure function.
   */
  static searchRulesToTagRules(grouped: TagSearchRule[]): ListTagRule[] {
    const flat: ListTagRule[] = [];
    for (const { lop, values } of grouped) {
      if (values.length === 0) {
        flat.push({ tag: '', rule: lop });
      } else {
        for (const tag of values) {
          flat.push({ tag, rule: lop });
        }
      }
    }
    return flat;
  }

  // --- Sort config conversion ---

  /** Combine sortType + sortDirection into a single UI key */
  static toSortUIType(sortType: string, sortDirection: string): string {
    return `${sortType}_${sortDirection}`;
  }

  /** Split a UI key back into sortType + sortDirection */
  static fromSortUIType(uiType: string): { sortType: string; sortDirection: string } {
    const option = LIST_SORT_UI_OPTIONS.find(o => o.value === uiType);
    if (option) return { sortType: option.sortType, sortDirection: option.sortDirection };
    return { sortType: 'published', sortDirection: 'desc' };
  }

  // --- Form data transforms ---

  /** Convert playlist data to settings form data (null → '' for form inputs) */
  static toSettingsFormData(playlist: AnyPlaylistData): PlaylistSettingsFormData {
    return {
      title: playlist.title,
      descShort: playlist.descShort ?? '',
      refId: playlist.refId ?? '',
      mediaStateType: playlist.mediaStateType as 'active' | 'inactive',
    };
  }

  /** Extract tag-specific content form data from a tag playlist */
  static toTagContentFormData(playlist: TagPlaylistData): TagPlaylistContentFormData {
    return {
      tagRules: PlaylistUtils.tagRulesToSearchRules(playlist.tagRules),
      sortUIType: PlaylistUtils.toSortUIType(playlist.sortType, playlist.sortDirection),
      limit: playlist.limit != null ? String(playlist.limit) : '',
    };
  }

  /** Build a full custom playlist update request from form data */
  static buildCustomUpdateRequest(
    settings: PlaylistSettingsFormData,
    mediaPositions: PlaylistMediaPosition[]
  ): UpdateCustomPlaylistRequest {
    return {
      mediaState: settings.mediaStateType as MediaStateType,
      title: settings.title,
      descShort: settings.descShort || null,
      refId: settings.refId || null,
      mediaPositions,
    };
  }

  /** Build a full tag playlist update request from form data */
  static buildTagUpdateRequest(
    settings: PlaylistSettingsFormData,
    content: TagPlaylistContentFormData
  ): UpdateTagPlaylistRequest {
    const { sortType, sortDirection } = PlaylistUtils.fromSortUIType(content.sortUIType);
    return {
      mediaState: settings.mediaStateType as MediaStateType,
      title: settings.title,
      descShort: settings.descShort || null,
      refId: settings.refId || null,
      limit: content.limit ? Number(content.limit) : null,
      sortType,
      sortDirection,
      tagRules: PlaylistUtils.searchRulesToTagRules(content.tagRules),
    };
  }

  // --- Type guards ---

  static isCustomPlaylist(p: AnyPlaylistData): p is CustomPlaylistData {
    return p.listType === 'custom';
  }

  static isTagPlaylist(p: AnyPlaylistData): p is TagPlaylistData {
    return p.listType === 'tag';
  }
}
